// src/utils/analytics.js
import { ANALYTICS_CONFIG } from '../config/analytics';

// Initialize analytics configuration
export const initializeAnalytics = () => {
  if (typeof window.gtag !== 'undefined' && window.UC_UI?.isInitialized()) {
    // Only initialize if Usercentrics is available and initialized
    debugGAConfig();
    
    // Configure cross-domain tracking
    window.gtag('config', ANALYTICS_CONFIG.measurementId, {
      debug_mode: ANALYTICS_CONFIG.debug,
      linker: {
        domains: ANALYTICS_CONFIG.domains,
        accept_incoming: true,
        decorate_forms: true,
        use_anchor: false,
        enhanced_measurement: true
      },
      cookie_flags: ANALYTICS_CONFIG.cookieFlags,
      cookie_domain: 'auto',
      allow_linker: true
    });

    // Enable auto-linking with enhanced settings
    window.gtag('linker:autoLink', ANALYTICS_CONFIG.domains, {
      accept_incoming: true,
      use_anchor: false,
      enhanced_measurement: true
    });
  } else {
    console.log('Analytics: gtag or UC_UI not available, analytics initialization skipped');
  }
};

// Enhanced cross-domain navigation handler
export const handleCrossDomainNavigation = (url) => {
  if (!url) return Promise.reject(new Error('No URL provided'));
  
  console.log('Analytics: Starting cross-domain navigation to:', url);
  
  return new Promise((resolve, reject) => {
    let decorationAttempts = 0;
    const maxAttempts = 3;
    const timeout = setTimeout(() => {
      reject(new Error('URL decoration timeout'));
    }, 2000); // 2 second timeout

    const attemptDecoration = () => {
      console.log(`Analytics: Decoration attempt ${decorationAttempts + 1}`);
      
      try {
        window.gtag('linker:decorate', {
          url,
          use_anchor: false,
          domains: ANALYTICS_CONFIG.domains
        });
        
        // Check if URL has _gl parameter after decoration
        const decoratedUrl = new URL(url);
        const hasGlParam = decoratedUrl.searchParams.has('_gl');
        
        if (hasGlParam) {
          console.log('Analytics: URL successfully decorated with _gl parameter');
          clearTimeout(timeout);
          resolve(decoratedUrl.toString());
          return;
        }
        
        // Try again if we haven't reached max attempts
        decorationAttempts++;
        if (decorationAttempts < maxAttempts) {
          setTimeout(attemptDecoration, 300); // Wait 300ms before trying again
        } else {
          console.warn('Analytics: Failed to decorate URL after max attempts');
          clearTimeout(timeout);
          resolve(url); // Return original URL if decoration fails
        }
      } catch (error) {
        console.error('Analytics: Error in URL decoration:', error);
        clearTimeout(timeout);
        trackCrossDomainError(error, url);
        reject(error);
      }
    };
    
    // Start decoration attempts if gtag is available
    if (typeof window.gtag !== 'undefined' && isAnalyticsConsented()) {
      attemptDecoration();
    } else {
      console.warn('Analytics: gtag not available or analytics not consented');
      clearTimeout(timeout);
      resolve(url); // Return original URL
    }
  });
};

// Check if analytics is consented in Usercentrics
export const isAnalyticsConsented = () => {
  try {
    if (window.UC_UI && window.UC_UI.getServicesBaseInfo) {
      const services = window.UC_UI.getServicesBaseInfo();
      const statisticsServices = services.filter(s => 
        s.categorySlug === 'statistics' && s.consent.status === true
      );
      return statisticsServices.length > 0;
    }
    return false;
  } catch (e) {
    console.error('Error checking analytics consent:', e);
    return false;
  }
};

// Track events with domain information
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined' && isAnalyticsConsented()) {
    const params = {
      ...eventParams,
      domain: window.location.hostname,
      page_location: window.location.href,
      send_to: ANALYTICS_CONFIG.measurementId
    };
    
    window.gtag('event', eventName, params);
    return true;
  }
  return false;
};

// Toggle debug mode for analytics
export const toggleDebugMode = (enable = true) => {
  if (typeof window.gtag !== 'undefined') {
    ANALYTICS_CONFIG.debug = enable;
    window.gtag('config', ANALYTICS_CONFIG.measurementId, {
      debug_mode: enable
    });
    console.log(`Analytics: Debug mode ${enable ? 'enabled' : 'disabled'}`);
    return true;
  }
  return false;
};

// Verify cross-domain tracking configuration
export const verifyCrossDomainTracking = () => {
  if (typeof window.gtag !== 'undefined') {
    const verification = {
      measurementId: ANALYTICS_CONFIG.measurementId,
      domains: ANALYTICS_CONFIG.domains,
      debug: ANALYTICS_CONFIG.debug,
      cookieFlags: ANALYTICS_CONFIG.cookieFlags,
      gtagAvailable: typeof window.gtag !== 'undefined',
      dataLayerAvailable: typeof window.dataLayer !== 'undefined',
      consentStatus: isAnalyticsConsented()
    };
    
    console.table(verification);
    return verification;
  }
  
  return {
    error: 'gtag not available',
    gtagAvailable: false,
    dataLayerAvailable: typeof window.dataLayer !== 'undefined'
  };
};

// Track cross-domain errors
export const trackCrossDomainError = (error, destination) => {
  if (typeof window.gtag !== 'undefined' && isAnalyticsConsented()) {
    window.gtag('event', 'cross_domain_error', {
      error_message: error.message || 'Unknown error',
      destination_domain: new URL(destination).hostname,
      source_domain: window.location.hostname,
      page_location: window.location.href,
      send_to: ANALYTICS_CONFIG.measurementId
    });
  }
};

// Debug GA configuration
export const debugGAConfig = () => {
  if (ANALYTICS_CONFIG.debug && typeof window.gtag !== 'undefined') {
    console.log('Analytics: Configuration', {
      measurementId: ANALYTICS_CONFIG.measurementId,
      domains: ANALYTICS_CONFIG.domains,
      cookieFlags: ANALYTICS_CONFIG.cookieFlags,
      debug: ANALYTICS_CONFIG.debug
    });
  }
};

// Track cross-domain navigation
export const trackCrossDomainNavigation = (destination, linkLocation) => {
  if (typeof window.gtag !== 'undefined' && isAnalyticsConsented()) {
    const destinationUrl = new URL(destination);
    const domain = destinationUrl.hostname;
    
    window.gtag('event', 'cross_domain_navigation', {
      destination_url: destination,
      destination_domain: domain,
      link_location: linkLocation || 'unspecified',
      page_location: window.location.href,
      timestamp: new Date().toISOString(),
      send_to: ANALYTICS_CONFIG.measurementId
    });
  }
};

// Track page view
export const trackPageView = (pageTitle, pageLocation) => {
  if (typeof window.gtag !== 'undefined' && isAnalyticsConsented()) {
    window.gtag('event', 'page_view', {
      page_title: pageTitle || document.title,
      page_location: pageLocation || window.location.href,
      send_to: ANALYTICS_CONFIG.measurementId
    });
  }
};

// Track user engagement
export const trackUserEngagement = (duration) => {
  if (typeof window.gtag !== 'undefined' && isAnalyticsConsented()) {
    window.gtag('event', 'user_engagement', {
      engagement_time_msec: duration,
      send_to: ANALYTICS_CONFIG.measurementId
    });
  }
};
